/** Legal pages (privacy, terms; refund and shipping for sites that sell) + cookie consent banner. Every kind of site. */
import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Checkbox, Col, Collapse, Form, Input, InputNumber, List, Result, Row, Segmented, Space, Spin, Switch, Tag, Typography, message } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../shared/i18n';
import { QUIET } from './api';
import { editorPath, toastErr, useSite } from './common';
import { useShopExt } from './ShopExtSettings';

const { Text } = Typography;
const KINDS = [{ value: 'privacy', label: 'Privacy policy' }, { value: 'terms', label: 'Terms of service' }, { value: 'refund', label: 'Refund policy' }, { value: 'shipping', label: 'Shipping policy' }];

const ShopLegal = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { api, siteId, site } = useSite();
    const sells = site?.kind === 'shop';
    const { s, setS, soon, save } = useShopExt();
    const [form] = Form.useForm();
    const [kind, setKind] = useState('privacy');
    const [preview, setPreview] = useState(null);
    const [kinds, setKinds] = useState(KINDS.map((k) => k.value));
    const [allowed, setAllowed] = useState(null);
    const kindList = KINDS.filter((k) => (allowed ? allowed.includes(k.value) : (sells || ['privacy', 'terms'].includes(k.value))));
    const [overwrite, setOverwrite] = useState(false);
    const [gen, setGen] = useState(null);
    const [busy, setBusy] = useState(false);
    const [cc, setCc] = useState({});

    useEffect(() => { if (s) { form.setFieldsValue(s.legal || {}); setCc(s.cookie_consent || {}); } }, [s, form]);
    useEffect(() => {
        if (!s || soon) return;
        setPreview(null);
        api.get('/legal/preview', { kind }, QUIET).then((p) => { setPreview(p); if (Array.isArray(p?.kinds)) setAllowed(p.kinds); }).catch(() => setPreview({ html: '' }));
    }, [api, kind, s, soon]);

    const saveDetails = async () => {
        const v = await form.validateFields();
        const r = await save({ legal: v });
        if (r) { message.success(t('Saved')); setPreview(null); api.get('/legal/preview', { kind }, QUIET).then(setPreview).catch(() => {}); }
    };
    const generate = async () => {
        setBusy(true);
        try { setGen(await api.post('/legal/pages', { kinds: kinds.filter((k) => kindList.some((x) => x.value === k)), overwrite })); } catch (e) { toastErr(e); } finally { setBusy(false); }
    };
    const applyConsent = async () => {
        try {
            const r = await api.post('/legal/cookie-consent', cc);
            setS((x) => ({ ...x, cookie_consent: r?.cookie_consent || cc }));
            message.success(r?.note || t('Saved'));
        } catch (e) { toastErr(e); }
    };

    if (soon) return <Result status="info" title={t('Coming soon')} />;
    if (!s) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;
    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Row gutter={16}>
                <Col xs={24} lg={10}>
                    <Card size="small" title={t('Business details')} extra={<Button size="small" type="primary" onClick={saveDetails}>{t('Save')}</Button>}>
                        <Form form={form} layout="vertical" requiredMark={false} size="small">
                            <Form.Item name="company" label={t('Legal name')}><Input /></Form.Item>
                            <Form.Item name="address" label={t('Address')}><Input.TextArea autoSize={{ minRows: 2 }} /></Form.Item>
                            <Space wrap>
                                <Form.Item name="email" label={t('Email', { context: 'shop' })} rules={[{ type: 'email' }]}><Input style={{ width: 200 }} /></Form.Item>
                                <Form.Item name="phone" label={t('Phone')}><Input style={{ width: 160 }} /></Form.Item>
                            </Space>
                            <Space wrap>
                                <Form.Item name="country" label={t('Governing law (country)')}><Input style={{ width: 200 }} /></Form.Item>
                                <Form.Item name="effective_date" label={t('Effective date')}><Input placeholder="2026-11-01" style={{ width: 140 }} /></Form.Item>
                            </Space>
                            {kindList.length > 2 && (
                                <Space wrap>
                                    <Form.Item name="return_days" label={t('Return window (days)')}><InputNumber min={0} max={365} /></Form.Item>
                                    <Form.Item name="processing_days" label={t('Processing time (days)')}><Input placeholder="1-3" style={{ width: 100 }} /></Form.Item>
                                </Space>
                            )}
                        </Form>
                    </Card>
                </Col>
                <Col xs={24} lg={14}>
                    <Card size="small" title={t('Policies')}>
                        <Segmented size="small" value={kind} onChange={setKind} options={kindList.map((k) => ({ value: k.value, label: t(k.label) }))} style={{ marginBottom: 12 }} />
                        <div style={{ border: '1px solid var(--ant-color-border-secondary, #f0f0f0)', borderRadius: 6, height: 300, overflow: 'hidden', background: '#fff' }}>
                            {!preview ? <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
                                : <iframe title="policy" sandbox="" srcDoc={`<!doctype html><meta charset="utf-8"><body style="font:14px/1.6 system-ui,sans-serif;padding:12px 16px;color:#111">${preview.html || ''}</body>`} style={{ width: '100%', height: '100%', border: 0 }} />}
                        </div>
                        <Space wrap style={{ marginTop: 12 }}>
                            <Checkbox.Group value={kinds.filter((k) => kindList.some((x) => x.value === k))} onChange={setKinds} options={kindList.map((k) => ({ value: k.value, label: t(k.label) }))} />
                        </Space>
                        <Space wrap style={{ marginTop: 8 }}>
                            <Checkbox checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)}>{t('Replace existing pages')}</Checkbox>
                            <Button type="primary" loading={busy} disabled={!kinds.some((k) => kindList.some((x) => x.value === k))} onClick={generate}>{t('Create pages')}</Button>
                        </Space>
                        {gen && (
                            <>
                                {gen.note && <Alert type="warning" showIcon style={{ marginTop: 12 }} message={gen.note} />}
                                <List size="small" style={{ marginTop: 8 }} dataSource={gen.pages || []} renderItem={(p) => (
                                    <List.Item actions={[<Button key="e" size="small" icon={<EditOutlined />} onClick={() => navigate(editorPath(siteId, p.page_id))}>{t('Edit')}</Button>]}>
                                        <Space><Text code>{p.path}</Text><Tag color={p.status === 'exists' ? 'default' : 'green'}>{t({ created: 'Created', updated: 'Updated', exists: 'Already exists' }[p.status] || p.status)}</Tag></Space>
                                    </List.Item>
                                )} />
                            </>
                        )}
                    </Card>
                </Col>
            </Row>
            <Card size="small" title={t('Cookie consent banner')} extra={<Button size="small" type="primary" onClick={applyConsent}>{t('Save')}</Button>}>
                <Space direction="vertical" style={{ width: '100%', maxWidth: 640 }}>
                    <Space><Switch checked={!!cc.enabled} onChange={(enabled) => setCc({ ...cc, enabled })} /><Text>{t('Show a cookie banner on every page')}</Text></Space>
                    {cc.enabled && (
                        <>
                            <Input.TextArea autoSize={{ minRows: 2 }} maxLength={500} value={cc.message} onChange={(e) => setCc({ ...cc, message: e.target.value })} />
                            <Space wrap>
                                <Input addonBefore={t('Accept')} value={cc.accept_label} onChange={(e) => setCc({ ...cc, accept_label: e.target.value })} style={{ width: 200 }} />
                                <Input addonBefore={t('Decline')} value={cc.decline_label} onChange={(e) => setCc({ ...cc, decline_label: e.target.value })} style={{ width: 200 }} />
                                <Input addonBefore={t('Policy link')} value={cc.policy_url} onChange={(e) => setCc({ ...cc, policy_url: e.target.value })} style={{ width: 280 }} />
                            </Space>
                            <Collapse ghost size="small" items={[{
                                key: 'labels', label: t('More labels'),
                                children: (
                                    <Space wrap>
                                        {[['settings_label', 'Settings'], ['save_label', 'Save choices'], ['necessary_label', 'Necessary'], ['analytics_label', 'Analytics'], ['marketing_label', 'Marketing'], ['reopen_label', 'Cookie settings']].map(([k, d]) => (
                                            <Input key={k} placeholder={t(d)} value={cc[k]} maxLength={40} onChange={(e) => setCc({ ...cc, [k]: e.target.value })} style={{ width: 200 }} />
                                        ))}
                                    </Space>
                                ),
                            }]} />
                        </>
                    )}
                </Space>
            </Card>
        </Space>
    );
};

export default ShopLegal;
